import { c as _c } from "react/compiler-runtime";
import Head from 'next/head';
import Image from 'next/image';
import { useState, useEffect } from 'react';
import classNames from 'classnames';
import Layout from '@/components/Layout';
import StructuredData from '@/components/StructuredData.js';
import Button from '@helios/Button';
import ButtonLink from '@helios/ButtonLink';
import Carousel from '@helios/Carousel';
import Container from '@helios/Container';
import FilterButtonGroup from '@helios/FilterButtonGroup';
import Grid from '@helios/Grid';
import IconButton from '@helios/IconButton';
import ProgramCard from '@helios/ProgramCard';
import SectionHeader from '@helios/SectionHeader';
import CTA from '@/components/CTA.js';
import ApplyNow from '@/components/ApplyNow.js';
import RegularAwardIcon from '@/components/icons/regular/award';
import RegularChevronDownIcon from '@/components/icons/regular/chevron-down';
import RegularChevronRightIcon from '@/components/icons/regular/chevron-right';
import RegularChevronUpIcon from '@/components/icons/regular/chevron-up';
import RegularCircleIcon from '@/components/icons/regular/circle';
import RegularGraduationCapIcon from '@/components/icons/regular/graduation-cap';
import RegularVideoIcon from '@/components/icons/regular/video';
import { allSites, hasHeaderTicker } from '@/utils/domains.js';
import { PROGRAMS_BY_PATH, getProgramsPageSchema } from '@/utils/seo/siteSchemas.js';
import { useSiteHrefs } from '@/utils/siteHref';
import styles from '@/styles/ProgramsPage.module.scss';
import AaBusinessAdmin from '@/public/images/aa-business-admin.jpg';
import AppliedAI from '@/public/images/applied-ai-program.jpg';
import DigitalMarketingConcentration from '@/public/images/digital-marketing-concentration/program-tile.webp';
import HcaiProgram from '@/public/images/hcai-program.jpg';
import AsIT from '@/public/images/as-it.jpg';
import CybersecurityProgram from '@/public/images/cybersecurity-program.jpg';
import CybersecurityAssociate from '@/public/images/cybersecurity-associate/program-tile.webp';
import ItaiProgram from '@/public/images/itai-program.jpg';
import AaParalegalStudies from '@/public/images/paralegal/program-tile.webp';
import Cosmetology from '@/public/images/cosmetology.png';
import Barbering from '@/public/images/barbering.png';
import CosmetologyAndBarbering from '@/public/images/cosmetology-and-barbering.png';
import MedicalAssisting from '@/public/images/medical-assistant.jpg';
import PhlebotomyCertificate from '@/public/images/medical-assisting.png';
import MedicalBillingAndCoding from '@/public/images/medical-billing-and-coding.png';
import Phlebotomy from '@/public/images/phlebotomy.png';
import VibeCodingHero from '@/public/images/vibecoding/homepage-banner.jpg';
import InformationTechnologyHero from '@/public/images/it-landing.jpg';
import BusinessAdministrationHero from '@/public/images/applied-ai-landing.jpg';
import HealthcareAdministrationHero from '@/public/images/healthcare-degree-carousel.jpg';

// The featured photo fills the 1024px column (the viewport less the gutters
// below it).
const FEATURED_IMAGE_SIZES = '(max-width: 767px) calc(100vw - 40px), (max-width: 1087px) calc(100vw - 64px), 1024px';

// The hero's featured programs (Phase 6 p01: the redesign's featured hero):
// the titles, pages and photos the "Featured programs" slides showed (the
// redesign's title has no closing full stop). Each one's type, format and
// length come from the program's entry in utils/seo/siteSchemas.js, which
// also feeds the page's JSON-LD.
const FEATURED_PROGRAMS = [{
  title: 'Online Associate Degree in Business Administration',
  href: '/online-associates-degree-in-business',
  image: BusinessAdministrationHero
}, {
  title: 'Online Associate Degree in Information Technology',
  href: '/online-degree-in-information-technology',
  image: InformationTechnologyHero
}, {
  title: 'Online Associate Degree in Healthcare Administration',
  href: '/online-healthcare-administration-associate-degree',
  image: HealthcareAdministrationHero
}];

// An ISO 8601 length in whole years ("P2Y") as words ("2 years").
function programLength(timeToComplete) {
  const years = Number(/^P(\d+)Y$/.exec(timeToComplete ?? '')?.[1]);
  if (!years) return null;
  return `${years} ${years === 1 ? 'year' : 'years'}`;
}

/** "Associate Degree · Online · 2 years", from the program's schema entry. */
export function featuredProgramMeta(href) {
  const program = PROGRAMS_BY_PATH[href];
  if (!program) return null;
  return [program.programType, program.educationalProgramMode, programLength(program.timeToComplete)].filter(Boolean).join(' · ');
}

// The filter icons keep the legacy glyph size (16px) in their own span: the
// label beside them is 13px.
function FilterIcon(t0) {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <span className={styles["filter-icon"]}>{children}</span>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
const PROGRAM_FILTER_OPTIONS = [{
  value: 'all',
  label: 'All',
  icon: <FilterIcon>
        <RegularCircleIcon />
      </FilterIcon>
}, {
  value: 'degrees',
  label: 'Degrees',
  icon: <FilterIcon>
        <RegularGraduationCapIcon />
      </FilterIcon>
}, {
  value: 'certificates',
  label: 'Certificates',
  icon: <FilterIcon>
        <RegularAwardIcon />
      </FilterIcon>
}, {
  value: 'programs',
  label: 'Courses',
  icon: <FilterIcon>
        <RegularVideoIcon />
      </FilterIcon>
}];
function programFilterFromRoute(initialPrograms, isSacramento) {
  if (initialPrograms.length === 0) {
    return isSacramento ? 'certificates' : 'all';
  }
  if (initialPrograms.includes('degrees') || initialPrograms.includes('associate-degrees')) {
    return 'degrees';
  }
  if (initialPrograms.includes('certificates') || initialPrograms.includes('certificate')) {
    return 'certificates';
  }
  if (initialPrograms.includes('programs')) {
    return 'programs';
  }
  return isSacramento ? 'certificates' : 'all';
}
export async function getStaticPaths() {
  return {
    paths: allSites.reduce((acc, site) => {
      return acc.concat(site !== 'sacramento' ? [{
        params: {
          site,
          program: false
        }
      }] : [{
        params: {
          site,
          program: false
        }
      }, {
        params: {
          site,
          program: ['associate-degrees']
        }
      }, {
        params: {
          site,
          program: ['certificate']
        }
      }]);
    }, []),
    fallback: false
  };
}
export async function getStaticProps({
  params
}) {
  return {
    props: {
      initialPrograms: params.program ?? [],
      site: params.site
    }
  };
}

/**
 * "See all" beside a section title: a text button (the legacy one was a
 * clickable div) that switches the filter to the section.
 */
function SeeAllButton(t0) {
  const $ = _c(6);
  const {
    onClick,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = classNames(styles["see-all"], className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <RegularChevronRightIcon className={styles["see-all-icon"]} />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== onClick || $[4] !== t1) {
    t3 = <Button className={t1} iconRight={t2} onClick={onClick} size="sm" tone="neutral" variant="text">See all</Button>;
    $[3] = onClick;
    $[4] = t1;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}

/**
 * The hero (Phase 6 p01): the redesign's featured programs on a Helios
 * Carousel `wide` under the filters and the "Featured programs" heading (an
 * h2, title only: no eyebrow labels, Alex on #1463). Each slide is the
 * program's photo under a scrim with the title (h3), the type, format and
 * length, and "Explore the program". Previous and next sit in the row under
 * the slides, at its ends, with the page dots between them (rule 3; Alex on
 * #1463: the arrows always in the dot row), so nothing covers the copy. The
 * redesign does not auto-advance this carousel, so neither does this one,
 * and its dots show no timer. The page's h1 stays "Browse programs".
 */
function FeaturedPrograms() {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <SectionHeader layout="stack" spacing="compact" title="Featured programs" titleAs="h2" titleScale="sm" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      previous: "Previous program",
      next: "Next program"
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className={styles.featured}>{t0}<Carousel controlLabels={t1} controlPlacement="below" controls="arrows" indicators="dots" label="Featured programs" slides={FEATURED_PROGRAMS.map(_temp)} variant="wide" /></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}

// Courses Section Component
function _temp(program, index) {
  return {
    key: program.href,
    content: <div className={styles["featured-slide"]}><Image alt={`${program.title} featured program`} className={styles["featured-image"]} fill={true} priority={index === 0} sizes={FEATURED_IMAGE_SIZES} src={program.image} /><div aria-hidden="true" className={styles["featured-shade"]} /><div className={styles["featured-copy"]}><h3 className={styles["featured-title"]}>{program.title}</h3><p className={styles["featured-meta"]}>{featuredProgramMeta(program.href)}</p><ButtonLink arrow={true} className={styles["featured-cta"]} href={program.href}>Explore the program</ButtonLink></div></div>
  };
}
function CoursesSection(t0) {
  const $ = _c(10);
  const {
    activeFilter,
    setActiveFilter
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <h2 className={styles["section-title"]}>Courses</h2>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== activeFilter || $[2] !== setActiveFilter) {
    t2 = activeFilter !== "programs" && <SeeAllButton onClick={e => {
      e.preventDefault();
      setActiveFilter("programs");
      window.scrollTo({
        top: 0,
        behavior: "smooth"
      });
    }} />;
    $[1] = activeFilter;
    $[2] = setActiveFilter;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <div className={styles["section-header"]}>{t1}{t2}</div>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      base: 1,
      sm: 3
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Grid columns={t4} gap="sm"><ProgramCard href="/vibe-coding" image={VibeCodingHero} imageAlt="Vibe Coding 101" title="Vibe Coding 101" type="Course" duration="8 Weeks" modality="Online" location="National" badge="New" /></Grid>;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t3) {
    t6 = <div className={styles.courses}>{t3}{t5}</div>;
    $[8] = t3;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
}
export function AssociateDegrees(t0) {
  const $ = _c(12);
  const {
    isSacramento: t1,
    activeFilter,
    setActiveFilter
  } = t0;
  const isSacramento = t1 === undefined ? false : t1;
  const toSite = useSiteHrefs();
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <h2 className={styles["section-title"]}>Degrees</h2>;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== activeFilter || $[2] !== setActiveFilter) {
    t3 = activeFilter !== "degrees" && <SeeAllButton onClick={e => {
      e.preventDefault();
      setActiveFilter("degrees");
      window.scrollTo({
        top: 0,
        behavior: "smooth"
      });
    }} />;
    $[1] = activeFilter;
    $[2] = setActiveFilter;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = <div className={styles["section-header"]}>{t2}{t3}</div>;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== isSacramento || $[7] !== toSite) {
    t5 = !isSacramento ? <Grid columns={{
      base: 1,
      sm: 3
    }} gap="sm"><ProgramCard href="/online-associates-degree-in-business" image={AaBusinessAdmin} imageAlt="Campus Online Business Program" title="A.A. Business Administration" type="Associate Degree" duration="2 years" modality="Online" location="National" /><ProgramCard href="/business-degree-applied-ai-concentration" image={AppliedAI} imageAlt="Applied AI Concentration in Business Administration" title="A.A. Business Administration with Applied AI Concentration" type="Associate Degree" duration="2 years" modality="Online" location="National" /><ProgramCard href="/business-degree-digital-marketing-concentration" image={DigitalMarketingConcentration} imageAlt="A.A. Business Administration with Digital Marketing and the Creator Economy Concentration" title="A.A. Business Administration with Digital Marketing and the Creator Economy Concentration" type="Associate Degree" duration="2 years" modality="Online" location="National" /><ProgramCard href="/online-degree-in-information-technology" image={AsIT} imageAlt="Associate Degree in Information Technology" title="A.S. Information Technology" type="Associate Degree" duration="2 years" modality="Online" location="National" /><ProgramCard href="/online-paralegal-degree" image={AaParalegalStudies} imageAlt="A.A. Paralegal Studies" title="A.A. Paralegal Studies" type="Associate Degree" duration="2 years" modality="Online" location="National" /><ProgramCard href="/information-technology-degree-cybersecurity-concentration" image={CybersecurityProgram} imageAlt="A.S. Information Technology with Cybersecurity Concentration" title="A.S. Information Technology with Cybersecurity Concentration" type="Associate Degree" duration="2 years" modality="Online" location="National" /><ProgramCard href="/information-technology-degree-applied-ai-concentration" image={ItaiProgram} imageAlt="A.S. Information Technology with Applied AI Concentration" title="A.S. Information Technology with Applied AI Concentration" type="Associate Degree" duration="2 years" modality="Online" location="National" /><ProgramCard href="/online-cybersecurity-associate-degree" image={CybersecurityAssociate} imageAlt="A.S. Cybersecurity" title="A.S. Cybersecurity" type="Associate Degree" duration="2 years" modality="Online" location="National" /><ProgramCard href="/online-healthcare-administration-associate-degree" image={HealthcareAdministrationHero} imageAlt="A.S. Healthcare Administration" title="A.S. Healthcare Administration" type="Associate Degree" duration="2 years" modality="Online" location="National" /><ProgramCard href="/healthcare-administration-degree-applied-ai-concentration" image={HcaiProgram} imageAlt="A.S. Healthcare Administration with Applied AI Concentration" title="A.S. Healthcare Administration with Applied AI Concentration" type="Associate Degree" duration="2 years" modality="Online" location="National" /></Grid> : <Grid columns={{
      base: 1,
      sm: 2,
      xl: 3
    }} gap="sm"><ProgramCard clientNavigation={false} href={toSite("campus", "/online-degree-in-information-technology")} image={AsIT} imageAlt="Associate Degree in Information Technology" title="A.S. Information Technology" type="Associate Degree" duration="2 years" modality="Online" location="National" /><ProgramCard clientNavigation={false} href={toSite("campus", "/online-healthcare-administration-associate-degree")} image={HealthcareAdministrationHero} imageAlt="A.S. Healthcare Administration" title="A.S. Healthcare Administration" type="Associate Degree" duration="2 years" modality="Online" location="National" /><ProgramCard clientNavigation={false} href={toSite("campus", "/online-paralegal-degree")} image={AaParalegalStudies} imageAlt="Associate Degree in Paralegal Studies" title="A.A. Paralegal Studies" type="Associate Degree" duration="2 years" modality="Online" location="National" /></Grid>;
    $[6] = isSacramento;
    $[7] = toSite;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t4 || $[10] !== t5) {
    t6 = <div className={styles.group}>{t4}{t5}</div>;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
}
export function CertificatePrograms() {
  const $ = _c(43);
  const toSite = useSiteHrefs();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <h2 className={styles["section-title"]}>Certificates</h2>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      base: 1,
      sm: 2,
      xl: 3
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== toSite) {
    t2 = toSite("sacramento", "/programs/paul-mitchell/barbering");
    $[2] = toSite;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <ProgramCard clientNavigation={false} href={t2} image={Barbering} imageAlt="Campus Barbering Program" title="Barbering" type="Certificate" duration="30-45 weeks" modality="In-Person" />;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== toSite) {
    t4 = toSite("sacramento", "/programs/paul-mitchell/cosmetology");
    $[6] = toSite;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t4) {
    t5 = <ProgramCard clientNavigation={false} href={t4} image={Cosmetology} imageAlt="Campus Cosmetology Program" title="Cosmetology" type="Certificate" duration="30-45 weeks" modality="In-Person" />;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== toSite) {
    t6 = toSite("sacramento", "/programs/paul-mitchell/cosmetology-and-barbering");
    $[10] = toSite;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t6) {
    t7 = <ProgramCard clientNavigation={false} href={t6} image={CosmetologyAndBarbering} imageAlt="Campus Cosmetology and Barbering Program" title="Cosmetology and Barbering" type="Certificate" duration="36-54 weeks" modality="In-Person" />;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== toSite) {
    t8 = toSite("sacramento", "/programs/healthcare/medical-assistant");
    $[14] = toSite;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t8) {
    t9 = <ProgramCard clientNavigation={false} href={t8} image={MedicalAssisting} imageAlt="Campus Medical Assistant Program" title="Medical Assistant" type="Certificate" duration="48 weeks" modality="Hybrid" />;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== toSite) {
    t10 = toSite("sacramento", "/programs/healthcare/medical-assisting-with-phlebotomy-certificate");
    $[18] = toSite;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t10) {
    t11 = <ProgramCard clientNavigation={false} href={t10} image={PhlebotomyCertificate} imageAlt="Campus Medical Assistant with Phlebotomy Program" title="Medical Assistant with Phlebotomy" type="Certificate" duration="72 weeks" modality="Hybrid" />;
    $[20] = t10;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== toSite) {
    t12 = toSite("sacramento", "/programs/healthcare/medical-billing-and-coding-professional");
    $[22] = toSite;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t12) {
    t13 = <ProgramCard clientNavigation={false} href={t12} image={MedicalBillingAndCoding} imageAlt="Campus Medical Billing and Coding Professional Program" title="Medical Billing and Coding Professional" type="Certificate" duration="60 weeks" modality="Hybrid" />;
    $[24] = t12;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== toSite) {
    t14 = toSite("sacramento", "/programs/healthcare/medical-billing-and-coding-specialist");
    $[26] = toSite;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t14) {
    t15 = <ProgramCard clientNavigation={false} href={t14} image={MedicalBillingAndCoding} imageAlt="Campus Medical Billing and Coding Specialist Program" title="Medical Billing and Coding Specialist" type="Certificate" duration="48 weeks" modality="Online" />;
    $[28] = t14;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== toSite) {
    t16 = toSite("sacramento", "/programs/healthcare/phlebotomy");
    $[30] = toSite;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== t16) {
    t17 = <ProgramCard clientNavigation={false} href={t16} image={Phlebotomy} imageAlt="Campus Phlebotomy Program" title="Phlebotomy" type="Certificate" duration="24 weeks" modality="In-Person" />;
    $[32] = t16;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== t11 || $[35] !== t13 || $[36] !== t15 || $[37] !== t17 || $[38] !== t3 || $[39] !== t5 || $[40] !== t7 || $[41] !== t9) {
    t18 = <div className={styles.group}>{t0}<Grid columns={t1} gap="sm">{t3}{t5}{t7}{t9}{t11}{t13}{t15}{t17}</Grid></div>;
    $[34] = t11;
    $[35] = t13;
    $[36] = t15;
    $[37] = t17;
    $[38] = t3;
    $[39] = t5;
    $[40] = t7;
    $[41] = t9;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  return t18;
}
function Programs(t0) {
  const $ = _c(66);
  const {
    initialPrograms: t1,
    site
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const initialPrograms = t2;
  const isSacramento = site === "sacramento";
  const pageTitle = isSacramento ? "Online Associate, Certificate & Training Programs | Campus.edu" : "Online Degree, Certificate & Training Programs | Campus.edu";
  const pageDescription = isSacramento ? "Campus delivers a range of online associates degrees, certification courses, and in-person vocational training to help you prepare for what's next." : "Explore online degree, certificate, and career training programs at Campus. Learn live from expert professors and build skills for what's next.";
  let t3;
  if ($[2] !== initialPrograms || $[3] !== isSacramento) {
    t3 = () => programFilterFromRoute(initialPrograms, isSacramento);
    $[2] = initialPrograms;
    $[3] = isSacramento;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const [activeFilter, setActiveFilter] = useState(t3);
  let t4;
  let t5;
  if ($[5] !== initialPrograms || $[6] !== isSacramento) {
    t4 = () => {
      setActiveFilter(programFilterFromRoute(initialPrograms, isSacramento));
    };
    t5 = [initialPrograms, isSacramento];
    $[5] = initialPrograms;
    $[6] = isSacramento;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  let programChildren;
  if ($[9] !== activeFilter || $[10] !== isSacramento) {
    programChildren = [];
    if (activeFilter === "degrees" || activeFilter === "all") {
      programChildren.push(<AssociateDegrees key="associate-degrees" isSacramento={isSacramento} activeFilter={activeFilter} setActiveFilter={setActiveFilter} />);
    }
    if (activeFilter === "certificates") {
      let t6;
      if ($[12] !== activeFilter) {
        t6 = <CertificatePrograms key="certificate-programs" setActiveFilter={setActiveFilter} activeFilter={activeFilter} />;
        $[12] = activeFilter;
        $[13] = t6;
      } else {
        t6 = $[13];
      }
      programChildren.push(t6);
    }
    if (activeFilter === "programs") {
      let t6;
      if ($[14] !== activeFilter) {
        t6 = <CoursesSection key="courses-section" setActiveFilter={setActiveFilter} activeFilter={activeFilter} />;
        $[14] = activeFilter;
        $[15] = t6;
      } else {
        t6 = $[15];
      }
      programChildren.push(t6);
    }
    $[9] = activeFilter;
    $[10] = isSacramento;
    $[11] = programChildren;
  } else {
    programChildren = $[11];
  }
  let t6;
  if ($[16] !== pageTitle) {
    t6 = <title>{pageTitle}</title>;
    $[16] = pageTitle;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  let t8;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <link rel="canonical" href="https://campus.edu/programs" />;
    t8 = <meta httpEquiv="X-UA-Compatible" content="IE=Edge" />;
    $[18] = t7;
    $[19] = t8;
  } else {
    t7 = $[18];
    t8 = $[19];
  }
  let t9;
  if ($[20] !== pageDescription) {
    t9 = <meta name="description" content={pageDescription} />;
    $[20] = pageDescription;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  let t11;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <meta name="keywords" content="Online Associate Degree in Business" />;
    t11 = <meta name="viewport" content="width=device-width, initial-scale=1" />;
    $[22] = t10;
    $[23] = t11;
  } else {
    t10 = $[22];
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t6 || $[25] !== t9) {
    t12 = <Head>{t6}{t7}{t8}{t9}{t10}{t11}</Head>;
    $[24] = t6;
    $[25] = t9;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== isSacramento) {
    t13 = !isSacramento && <StructuredData id="programs-page-schema" data={getProgramsPageSchema()} />;
    $[27] = isSacramento;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== site) {
    t14 = hasHeaderTicker(site);
    $[29] = site;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  const t15 = !t14;
  let t16;
  if ($[31] !== t15) {
    t16 = classNames(styles.page, {
      [styles["page--flush"]]: t15
    });
    $[31] = t15;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  const t17 = isSacramento ? styles.title : styles["visually-hidden"];
  let t18;
  if ($[33] !== t17) {
    t18 = <h1 className={t17}>Browse programs</h1>;
    $[33] = t17;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = value => {
      setActiveFilter(value);
    };
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  let t20;
  let t21;
  if ($[36] !== activeFilter) {
    t20 = <div className={styles.filters}><FilterButtonGroup options={PROGRAM_FILTER_OPTIONS} value={activeFilter} ariaLabel="Program type" onChange={t19} /></div>;
    t21 = activeFilter === "all" && <FeaturedPrograms />;
    $[36] = activeFilter;
    $[37] = t20;
    $[38] = t21;
  } else {
    t20 = $[37];
    t21 = $[38];
  }
  let t22;
  if ($[39] !== activeFilter || $[40] !== isSacramento) {
    t22 = activeFilter === "all" && <ExploreMorePrograms isSacramento={isSacramento} activeFilter={activeFilter} seeAll={() => {
      setActiveFilter("certificates");
      window.scrollTo({
        top: 0,
        behavior: "smooth"
      });
    }} />;
    $[39] = activeFilter;
    $[40] = isSacramento;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] !== activeFilter || $[43] !== isSacramento) {
    t23 = activeFilter === "all" && <CoursesSection isSacramento={isSacramento} activeFilter={activeFilter} setActiveFilter={setActiveFilter} />;
    $[42] = activeFilter;
    $[43] = isSacramento;
    $[44] = t23;
  } else {
    t23 = $[44];
  }
  let t24;
  if ($[45] !== programChildren || $[46] !== t18 || $[47] !== t20 || $[48] !== t21 || $[49] !== t22 || $[50] !== t23) {
    t24 = <Container><div className={styles.column}>{t18}{t20}{t21}{programChildren}{t22}{t23}</div></Container>;
    $[45] = programChildren;
    $[46] = t18;
    $[47] = t20;
    $[48] = t21;
    $[49] = t22;
    $[50] = t23;
    $[51] = t24;
  } else {
    t24 = $[51];
  }
  let t25;
  if ($[52] !== t16 || $[53] !== t24) {
    t25 = <div className={t16}>{t24}</div>;
    $[52] = t16;
    $[53] = t24;
    $[54] = t25;
  } else {
    t25 = $[54];
  }
  let t26;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <CTA />;
    $[55] = t26;
  } else {
    t26 = $[55];
  }
  let t27;
  if ($[56] !== isSacramento) {
    t27 = !isSacramento && <ApplyNow />;
    $[56] = isSacramento;
    $[57] = t27;
  } else {
    t27 = $[57];
  }
  let t28;
  if ($[58] !== t12 || $[59] !== t13 || $[60] !== t25 || $[61] !== t27) {
    t28 = <div id="wrapper">{t12}{t13}{t25}{t26}{t27}</div>;
    $[58] = t12;
    $[59] = t13;
    $[60] = t25;
    $[61] = t27;
    $[62] = t28;
  } else {
    t28 = $[62];
  }
  let t29;
  if ($[63] !== site || $[64] !== t28) {
    t29 = <Layout site={site}>{t28}</Layout>;
    $[63] = site;
    $[64] = t28;
    $[65] = t29;
  } else {
    t29 = $[65];
  }
  return t29;
}

// Certificate programs for the strip (the five non-Sacramento hosts): their
// pages are Sacramento's, linked through useSiteHrefs at render.
const CERTIFICATE_PROGRAMS = [{
  title: 'Barbering',
  type: 'Certificate',
  duration: '30-45 weeks',
  location: 'In-Person',
  path: '/programs/paul-mitchell/barbering',
  image: Barbering
}, {
  title: 'Cosmetology',
  type: 'Certificate',
  duration: '30-45 weeks',
  location: 'In-Person',
  path: '/programs/paul-mitchell/cosmetology',
  image: Cosmetology
}, {
  title: 'Cosmetology and Barbering',
  type: 'Certificate',
  duration: '36-54 weeks',
  location: 'In-Person',
  path: '/programs/paul-mitchell/cosmetology-and-barbering',
  image: CosmetologyAndBarbering
}, {
  title: 'Medical Assistant',
  type: 'Certificate',
  duration: '48 weeks',
  modality: 'Hybrid',
  path: '/programs/healthcare/medical-assistant',
  image: MedicalAssisting
}, {
  title: 'Medical Assistant with Phlebotomy',
  type: 'Certificate',
  duration: '72 weeks',
  modality: 'Hybrid',
  path: '/programs/healthcare/medical-assisting-with-phlebotomy-certificate',
  image: PhlebotomyCertificate
}, {
  title: 'Medical Billing and Coding Professional',
  type: 'Certificate',
  duration: '60 weeks',
  modality: 'Hybrid',
  path: '/programs/healthcare/medical-billing-and-coding-professional',
  image: MedicalBillingAndCoding
}, {
  title: 'Medical Billing and Coding Specialist',
  type: 'Certificate',
  duration: '48 weeks',
  modality: 'Online',
  path: '/programs/healthcare/medical-billing-and-coding-specialist',
  image: MedicalBillingAndCoding
}, {
  title: 'Phlebotomy',
  type: 'Certificate',
  duration: '24 weeks',
  location: 'In-Person',
  path: '/programs/healthcare/phlebotomy',
  image: Phlebotomy
}];
function ExploreMorePrograms(t0) {
  const $ = _c(17);
  const {
    isSacramento: t1,
    seeAll,
    activeFilter
  } = t0;
  const isSacramento = t1 === undefined ? false : t1;
  const toSite = useSiteHrefs();
  const [collapsed, setCollapsed] = useState(false);
  let t2;
  if ($[0] !== activeFilter || $[1] !== isSacramento || $[2] !== seeAll) {
    t2 = !isSacramento && <div className={styles["section-header"]}><h2 className={styles["section-title"]}>Certificates</h2>{activeFilter !== "certificates" && <SeeAllButton className={styles["see-all--from-md"]} onClick={seeAll} />}</div>;
    $[0] = activeFilter;
    $[1] = isSacramento;
    $[2] = seeAll;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== collapsed || $[5] !== isSacramento || $[6] !== toSite) {
    t3 = isSacramento && <div className={styles["explore-panel"]}><header className={styles["explore-panel-header"]}><h3 className={styles["explore-panel-title"]}>Explore more programs</h3><IconButton icon={collapsed ? <RegularChevronDownIcon /> : <RegularChevronUpIcon />} label={collapsed ? "Expand programs" : "Collapse programs"} size="lg" onClick={() => setCollapsed(_temp2)} /></header>{!collapsed && isSacramento && <div className={styles["explore-panel-list"]}><ProgramCard clientNavigation={false} layout="list" href={toSite("campus", "/online-associates-degree-in-business")} title="A.A. Business Administration" type="Associate Degree" duration="2 years" modality="Online" location="National" /><ProgramCard clientNavigation={false} layout="list" href={toSite("campus", "/business-degree-applied-ai-concentration")} title="A.A. Business Administration with Applied AI Concentration" type="Associate Degree" duration="2 years" modality="Online" location="National" /></div>}</div>;
    $[4] = collapsed;
    $[5] = isSacramento;
    $[6] = toSite;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== activeFilter || $[9] !== isSacramento || $[10] !== seeAll || $[11] !== toSite) {
    t4 = !isSacramento && <><Carousel label="Certificates" slides={CERTIFICATE_PROGRAMS.map((program, index) => ({
        key: `${index}`,
        content: <ProgramCard clientNavigation={false} href={toSite("sacramento", program.path)} image={program.image} imageAlt={`${program.title} Program`} title={program.title} type={program.type} duration={program.duration} modality={program.modality} location={program.location} />
      }))} variant="strip" />{activeFilter !== "certificates" && <SeeAllButton className={styles["see-all--below-md"]} onClick={seeAll} />}</>;
    $[8] = activeFilter;
    $[9] = isSacramento;
    $[10] = seeAll;
    $[11] = toSite;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t2 || $[14] !== t3 || $[15] !== t4) {
    t5 = <div className={styles.explore}>{t2}{t3}{t4}</div>;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return t5;
}
function _temp2(c) {
  return !c;
}
export default Programs;